import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { BoutonPrincipal, BoutonSecondaire, Etiquette } from './ui/Champ';
import { MessageErreur } from './ui/MessageEtat';
import { erreurGlobale } from './ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';

/**
 * Export comptable mensuel (specs F5).
 *
 * ÉCRAN DE CONTRÔLE avant dépôt : on montre ce qui partira, et surtout si
 * l'équilibre débit/crédit est respecté. Un fichier déséquilibré est rejeté
 * par la comptabilité, et le découvrir chez l'expert-comptable coûte plusieurs
 * jours d'aller-retour.
 *
 * Le périmètre n'est pas « le mois dernier » mais « ce qui n'est jamais
 * parti » : une note approuvée le 3 ne doit pas être manquée, une note restée
 * deux mois en attente ne doit pas partir deux fois.
 */
export function ExportComptable() {
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'export'],
    queryFn: () => ndfApi.exportComptable.apercu(),
    retry: false,
  });

  const deposer = useMutation({
    mutationFn: () => ndfApi.exportComptable.deposer(),
    onSuccess: (res) => {
      toast.success(`Export ${res.numero} déposé (${res.notes} note(s), ${res.lignes} lignes).`);
      qc.invalidateQueries({ queryKey: ['ndf', 'export'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'Le dépôt a échoué.'),
  });

  if (isLoading) return <p className="text-sm text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;
  if (!data) return null;

  const rienAExporter = data.nombre_depenses === 0;
  const bloque = data.desequilibres.length > 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-nuit/70">
        L’export reprend les notes approuvées <strong>jamais transmises</strong> : quelle que soit
        leur date. Une note restée en attente deux mois ne part donc qu’une fois, et une note
        approuvée hier n’est pas oubliée.
      </p>

      {rienAExporter ? (
        <p className="rounded-lg border border-dashed border-nuit/25 bg-white p-6 text-center text-sm text-nuit/70">
          Aucune note à exporter : tout a déjà été transmis.
        </p>
      ) : (
        <section className="space-y-3 rounded-lg border border-nuit/10 bg-white p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-titre font-bold text-nuit">{data.numero}</h3>
            <Etiquette ton={bloque ? 'alerte' : 'positif'}>
              {bloque ? `${data.desequilibres.length} écriture(s) déséquilibrée(s)` : 'Équilibré'}
            </Etiquette>
          </div>

          <dl className="space-y-1 text-sm">
            <Ligne libelle="Notes concernées" valeur={String(data.notes.length)} />
            <Ligne libelle="Dépenses" valeur={String(data.nombre_depenses)} />
            <Ligne libelle="Lignes d’écriture" valeur={String(data.nombre_lignes)} />
            <Ligne libelle="Total débit" valeur={euros(data.total_debit)} />
            <Ligne libelle="Total crédit" valeur={euros(data.total_credit)} />
          </dl>

          <ul className="flex flex-wrap gap-1">
            {data.notes.map((n) => (
              <li key={n.id}>
                <Etiquette>{n.numero}</Etiquette>
              </li>
            ))}
          </ul>

          {/* Un déséquilibre BLOQUE : mieux vaut refuser que de déposer un
              fichier que la comptabilité rejettera. */}
          {bloque && (
            <p className="rounded-md border border-corail-fond/60 border-l-4 border-l-corail-fond p-3 text-sm text-corail">
              Les écritures {data.desequilibres.map((d) => d.ordre).join(', ')} ne s’équilibrent pas.
              Le dépôt est bloqué tant qu’elles ne sont pas corrigées : un fichier déséquilibré
              serait rejeté par la comptabilité.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <BoutonPrincipal
              type="button"
              disabled={bloque || deposer.isPending}
              onClick={() => deposer.mutate()}
            >
              {deposer.isPending ? 'Dépôt…' : 'Déposer dans le dossier KPMG'}
            </BoutonPrincipal>
            {/* Relire avant d'envoyer, ou transmettre à la main si Dropbox est
                indisponible. */}
            <a
              href="/api/ndf/export/fichier"
              className="flex min-h-11 items-center rounded-lg border border-nuit/20 px-4 text-sm font-medium text-nuit"
            >
              Télécharger sans déposer
            </a>
          </div>
        </section>
      )}
    </div>
  );
}

function euros(v: number) {
  return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function Ligne({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-nuit/5 pb-1 last:border-0">
      <dt className="text-nuit/60">{libelle}</dt>
      <dd className="text-right font-medium text-nuit">{valeur}</dd>
    </div>
  );
}
